import { Button, Tabs } from '@heroui/react'
import type { StaffKey } from '../lib/staffText'
import { Icon, type IconName } from '../ui/icons'
import { useStaffText } from '../ui/StaffText'
import { Find } from './settingsKit'
import '../styles/settings.css'

export interface MenuEntry {
  key: string
  label: StaffKey
  icon: IconName
  /** A sub-heading inside the group (Advanced). */
  part?: StaffKey
  /** A page of its own outside Settings. */
  page?: boolean
}

export interface MenuGroup {
  key: string
  label: StaffKey
  entries: MenuEntry[]
}

export interface FoundRow {
  entry: MenuEntry
  group: MenuGroup
}

// With no section open (only pages of their own to point at), no tab is chosen.
const NONE = '\u0000'

/**
 * Settings' left menu: one vertical HeroUI Tabs list, the groups' names as headings in it
 * (disabled tabs, so the arrows skip them). A search at the top lists matches instead.
 */
export function SettingsMenu({
  groups,
  currentKey,
  filter,
  onFilter,
  found,
  label,
  word,
  onOpen,
}: {
  groups: MenuGroup[]
  currentKey: string
  filter: string
  onFilter: (q: string) => void
  found: FoundRow[]
  label: string
  /** Words for labels: the installation owner's pages stay English. */
  word: (k: StaffKey) => string
  onOpen: (entry: MenuEntry) => void
}) {
  const rows = groups.flatMap((g) => [
    { kind: 'head' as const, id: `group:${g.key}`, text: word(g.label) },
    ...g.entries.flatMap((e, i) => [
      ...(e.part && e.part !== g.entries[i - 1]?.part ? [{ kind: 'part' as const, id: `part:${g.key}:${e.part}`, text: word(e.part) }] : []),
      { kind: 'entry' as const, id: e.key, entry: e },
    ]),
  ])
  const entries = groups.flatMap((g) => g.entries)
  const disabled = rows.filter((r) => r.kind !== 'entry').map((r) => r.id)
  const { say } = useStaffText()
  const q = filter.trim()

  return (
    <nav className="set-menu" aria-label={label}>
      <Find className="set-menu__find" value={filter} onChange={onFilter} onSubmit={() => found[0] && onOpen(found[0].entry)} label={say('admin.find')} />
      {q ? (
        found.length > 0 ? (
          <ul className="set-menu__found">
            {found.map(({ entry, group }) => (
              <li key={entry.key}>
                <Button variant="ghost" size="sm" className="set-menu__hit" onPress={() => onOpen(entry)}>
                  <Icon name={entry.icon} size={16} />
                  <span>
                    {word(entry.label)}
                    <small>{word(group.label)}</small>
                  </span>
                  {entry.page && <Icon name="arrowUpRight" size={14} />}
                </Button>
              </li>
            ))}
          </ul>
        ) : (
          <p className="set-menu__none">{say('admin.findNone', { q })}</p>
        )
      ) : (
      <Tabs
        orientation="vertical"
        variant="secondary"
        align="start"
        keyboardActivation="manual"
        selectedKey={entries.some((e) => !e.page && e.key === currentKey) ? currentKey : NONE}
        disabledKeys={disabled}
        onSelectionChange={(key) => {
          const entry = entries.find((e) => e.key === key)
          if (entry) onOpen(entry)
        }}
      >
        <Tabs.ListContainer>
          <Tabs.List aria-label={label}>
            {rows.map((r) =>
              r.kind === 'entry' ? (
                <Tabs.Tab key={r.id} id={r.id}>
                  {/* The open section's tab fires no selection change; a click on it still returns to its list. */}
                  <span className="set-menu__item" onClick={r.entry.key === currentKey ? () => onOpen(r.entry) : undefined}>
                    <Icon name={r.entry.icon} size={16} />
                    <span>{word(r.entry.label)}</span>
                    {r.entry.page && <Icon name="arrowUpRight" size={14} />}
                  </span>
                  <Tabs.Indicator />
                </Tabs.Tab>
              ) : (
                <Tabs.Tab key={r.id} id={r.id} className={r.kind === 'head' ? 'set-menu__name' : 'set-menu__part'}>
                  {r.text}
                </Tabs.Tab>
              ),
            )}
          </Tabs.List>
        </Tabs.ListContainer>
      </Tabs>
      )}
    </nav>
  )
}
